import { Controller } from "stimulus";
import { API_ENDPOINTS } from "../config";

import { debounce } from "../util";

const MINIMUM_QUERY_LENGTH = 1;
const INPUT_DEBOUNCE_DURATION_MS = 400;

const isDownKey = keyboardEvent => {
  return keyboardEvent.key === "ArrowDown" || keyboardEvent.key === "Down";
};
const isUpKey = keyboardEvent => {
  return keyboardEvent.key === "ArrowUp" || keyboardEvent.key === "Up";
};
const isEscKey = keyboardEvent => {
  return keyboardEvent.key === "Escape" || keyboardEvent.key === "Esc";
};
const isEnterKey = keyboardEvent => {
  return keyboardEvent.key === "Enter";
};

const liTemplate = (id, dataType, imgSrc, itemText) => `
  <li data-id="${id}" data-type="${dataType}" class="d-flex align-items-center p-2" style="line-height: 1">
    ${
      imgSrc
        ? `<img class="mr-2" src="${imgSrc}" width="15" height="15" />`
        : `<div class="mr-2" style="width: 15px; height: 15px;"></div>`
    }
    ${itemText}
  </li>`;

export default class extends Controller {
  static targets = ["results", "id", "type", "form", "progress"];

  connect() {
    this._debouncedFetchResults = debounce(
      this._fetchResults.bind(this),
      INPUT_DEBOUNCE_DURATION_MS
    );

    this.locale = document.body.getAttribute("data-locale");
  }

  onInputChanged(e) {
    const query = e.target.value;
    this._debouncedFetchResults(query);
  }

  onKeydown(e) {
    if (isEscKey(e)) {
      this.resultsTarget.style.display = "none"; // Hide results
      this.resultsTarget.innerHTML = ""; // Clear results
    } else if (isEnterKey(e)) {
      const selectedItem = this.resultsTarget.querySelector("li.selected");
      if (selectedItem) {
        this._submitForm(selectedItem);
      } else {
        // Hitting enter when no results present
        const query = e.target.value;
        this._debouncedFetchResults(query);
      }
    } else if (isUpKey(e) || isDownKey(e)) {
      e.preventDefault();
      e.stopPropagation();

      // Set the next/previous result as selected
      const selectedItem = this.resultsTarget.querySelector("li.selected");
      if (!selectedItem) {
        return;
      }

      const items = Array.from(this.resultsTarget.querySelectorAll("li"));
      const selectedItemIdx = items.findIndex(item => item === selectedItem);

      let newSelectedItem;
      let newSelectedItemIdx = 0;
      if (isDownKey(e)) {
        newSelectedItemIdx = selectedItemIdx + 1;
        if (newSelectedItemIdx < items.length) {
          selectedItem.classList.remove("selected");

          newSelectedItem = items[newSelectedItemIdx];
          newSelectedItem.classList.add("selected");
        }
      } else {
        newSelectedItemIdx = selectedItemIdx - 1;
        if (newSelectedItemIdx >= 0) {
          selectedItem.classList.remove("selected");

          newSelectedItem = items[newSelectedItemIdx];
          newSelectedItem.classList.add("selected");
        }
      }

      if (newSelectedItemIdx === 0) {
        this.resultsTarget.scrollTop = 0;
      }

      if (!newSelectedItem) {
        return;
      }

      //
      // Scroll item into visible view.
      //

      const scrollContainer = this.resultsTarget.querySelector(".scroll-container");
      const scrollContainerTop = scrollContainer.getBoundingClientRect().top;

      const containerTop = this.resultsTarget.getBoundingClientRect().top;
      const containerHeight = this.resultsTarget.getBoundingClientRect().height;

      const itemTop = newSelectedItem.getBoundingClientRect().top;
      const itemHeight = newSelectedItem.getBoundingClientRect().height;

      const itemTopToScrollContainerTopDistance = itemTop - scrollContainerTop;

      let itemTopTarget = itemTop;
      if (isDownKey(e)) {
        // Bottom of the box
        itemTopTarget = containerTop + containerHeight - itemHeight;
      } else {
        // Top of the box
        itemTopTarget = containerTop;
      }

      const scrollTop = itemTopToScrollContainerTopDistance - (itemTopTarget - containerTop);

      if (isDownKey(e)) {
        if (itemTop > itemTopTarget) {
          this.resultsTarget.scrollTop = scrollTop;
        }
      } else {
        if (itemTop < itemTopTarget) {
          this.resultsTarget.scrollTop = scrollTop;
        }
      }
    }
  }

  onClick(e) {
    const selectedItem = this.resultsTarget.querySelector("li.selected");
    if (selectedItem && (e.target.nodeName === "LI" || e.target.nodeName === "IMG")) {
      this._submitForm(selectedItem);
    }
  }

  onMousedown(e) {
    if (e.target.nodeName === "LI" || e.target.nodeName === "IMG") {
      this._isClickInProcess = true;
    }
  }

  onMouseup(e) {
    this._isClickInProcess = false;
  }

  onMouseover(e) {
    const selectedItem = this.resultsTarget.querySelector("li.selected");

    let liTarget;
    if (e.target.nodeName === "LI") {
      liTarget = e.target;
    } else if (e.target.nodeName === "IMG") {
      liTarget = e.target.parentElement;
    }

    if (selectedItem && liTarget) {
      selectedItem.classList.remove("selected");
      liTarget.classList.add("selected");
    }
  }

  onFocus(e) {
    if (e.target.value && this.resultsTarget.children.length) {
      this.resultsTarget.style.display = "";
    }
  }

  onBlur(e) {
    if (!this._isClickInProcess) {
      this.resultsTarget.style.display = "none";
    }
  }

  _submitForm(selectedItem) {
    this.idTarget.value = selectedItem.getAttribute("data-id");
    this.typeTarget.value = selectedItem.getAttribute("data-type");
    this.formTarget.submit();
  }

  _fetchResults(query) {
    query = query && query.trim();
    if (!query || query.length < MINIMUM_QUERY_LENGTH) {
      return {
        coins: [],
        icos: [],
        exchanges: []
      };
    }

    this._latestQuery = query;
    this.progressTarget.style.display = "block";
    fetch(
      `${API_ENDPOINTS[process.env.NODE_ENV]}/search?query=${window.encodeURIComponent(
        query
      )}&locale=${this.locale}`,
      { credentials: "same-origin" }
    )
      .then(response => response.json())
      .then(json => {
        if (query === this._latestQuery) {
          this.progressTarget.style.display = "";
          // Only update if matches latest query.
          // Race condition when earlier query returns earlier that latest query.
          this._renderResults(json);
        }
      })
      .fail(() => {
        this.progressTarget.style.display = "";
        this._renderResults({}, true);
      });
  }

  _renderResults(results, error = false) {
    if (error) {
      this.resultsTarget.innerHTML = '<div class="p-2">An error occured. Try again later.</div>';
      this.resultsTarget.style.display = "";
      return;
    }

    // Generate the initial static template for this result.
    let resultsTemplate = "";
    if (results.coins.length) {
      resultsTemplate = `
        <div class="py-1 px-2 font-semibold">COINS</div>
        <ul class="list-reset">
          ${results.coins
            .map((coin, idx) => {
              return liTemplate(coin.id, "coin", coin.thumb, `${coin.name} (${coin.symbol})`);
            })
            .join("")}
        </ul>`;
    }
    if (results.icos.length) {
      resultsTemplate += `
        <div class="py-1 px-2 font-semibold">ICO</div>
        <ul class="list-reset">
          ${results.icos
            .map((ico_coin, idx) => {
              return liTemplate(
                ico_coin.id,
                "ico",
                ico_coin.thumb,
                `${ico_coin.name} (${ico_coin.symbol})`
              );
            })
            .join("")}
        </ul>`;
    }
    if (results.exchanges.length) {
      resultsTemplate += `
        <div class="py-1 px-2 font-semibold">EXCHANGES</div>
        <ul class="list-reset">
          ${results.exchanges
            .map((exchange, idx) => {
              return liTemplate(exchange.id, "exchange", exchange.thumb, exchange.name);
            })
            .join("")}
        </ul>`;
    }
    if (!resultsTemplate) {
      resultsTemplate = '<div class="p-2">No Results Found</div>';
    }

    this.resultsTarget.innerHTML = `
      <div class="scroll-container">
        ${resultsTemplate}
      </div>`;

    this.resultsTarget.scrollTop = 0;
    this.resultsTarget.style.display = ""; // IE11 needs "", not null

    const firstResult = this.resultsTarget.querySelector("li");
    if (firstResult) {
      firstResult.classList.add("selected");
    }
  }
}



// WEBPACK FOOTER //
// ./app/assets/webpack/controllers/search_controller.js